import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  /** Ikona před textem (lucide, velikost 16–18). */
  icon?: ReactNode
  /** Ikona za textem. */
  iconRight?: ReactNode
  /** Plná šířka jen na mobilu (pod sm), na větších displejích přirozená šířka. */
  fullWidthMobile?: boolean
  /** Plná šířka vždy. */
  block?: boolean
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white shadow-sm hover:bg-brand-700 active:bg-brand-800 focus-visible:ring-brand-500/40',
  secondary: 'bg-white text-ink-800 border border-slate-300 shadow-sm hover:bg-slate-50 hover:border-slate-400 active:bg-slate-100 focus-visible:ring-slate-400/40',
  ghost: 'text-ink-700 hover:bg-slate-100 active:bg-slate-200 focus-visible:ring-slate-400/40',
  danger: 'bg-red-700 text-white shadow-sm hover:bg-red-800 active:bg-red-900 focus-visible:ring-red-500/40',
  success: 'bg-emerald-700 text-white shadow-sm hover:bg-emerald-800 active:bg-emerald-900 focus-visible:ring-emerald-500/40',
}

// Výšky: sm 36 px (husté tabulky na desktopu), md 44 px (výchozí dotyková plocha), lg 48 px (hlavní akce na mobilu).
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-4 text-sm gap-2',
  lg: 'h-12 px-5 text-base gap-2',
}

/**
 * Tlačítko aplikace. Primární (červené) jen pro hlavní akci obrazovky — jedna na pohled.
 * Ostatní akce `secondary`, méně důležité `ghost`, nevratné `danger`.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, icon, iconRight, fullWidthMobile, block, className, children, disabled, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      {...props}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={clsx(
        'inline-flex select-none items-center justify-center whitespace-nowrap rounded-lg font-semibold transition-colors',
        'focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        fullWidthMobile && 'w-full sm:w-auto',
        className,
      )}
    >
      {loading ? <Spinner className="!h-4 !w-4" tone={variant === 'secondary' || variant === 'ghost' ? 'brand' : 'white'} /> : icon}
      {children}
      {iconRight}
    </button>
  )
})

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Povinný popisek pro čtečky — ikona sama nic neříká. */
  label: string
  icon: ReactNode
  variant?: 'ghost' | 'secondary' | 'primary' | 'danger'
  /** `sm` = 36 px vizuálně, ale dotyková plocha zůstává 44 px (neviditelný přesah). */
  size?: 'sm' | 'md'
}

const ICON_VARIANTS = {
  ghost: 'text-ink-600 hover:bg-slate-100 hover:text-ink-900 active:bg-slate-200',
  secondary: 'bg-white text-ink-700 border border-slate-300 shadow-sm hover:bg-slate-50',
  primary: 'bg-brand-600 text-white shadow-sm hover:bg-brand-700',
  danger: 'text-red-700 hover:bg-red-50 active:bg-red-100',
}

/**
 * Tlačítko jen s ikonou. Dotyková plocha vždy aspoň 44 × 44 px. Popisek (`label`) jde do
 * aria-label; pokud je význam ikony pro uživatele nejasný, patří vedle ikony i viditelný text.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, variant = 'ghost', size = 'md', className, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      {...props}
      className={clsx(
        'relative inline-flex shrink-0 items-center justify-center rounded-lg transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-40',
        size === 'sm' ? 'h-9 w-9 before:absolute before:-inset-1 before:content-[""]' : 'h-11 w-11',
        ICON_VARIANTS[variant],
        className,
      )}
    >
      {icon}
    </button>
  )
})
